import { useEffect, useMemo, useState } from "react";
import { GiFarmTractor } from "react-icons/gi";
import { LuCopyPlus } from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import AjudaTela from "../../components/AjudaTela";
import Layout from "../../components/Layout";
import ModalCadastroFazenda from "../../components/modais/ModalCadastroFazenda";
import ModalCadastroTipoMaquina from "../../components/modais/ModalCadastroTipoMaquina";
import ModalCadastroFabricanteMaquina from "../../components/modais/ModalCadastroFabricanteMaquina";
import {
  repositorioFazenda,
  repositorioMaquina,
  repositorioTipoMaquina,
  repositorioFabricanteMaquina,
} from "../../sync/repositories/fazendas";
import { extrairMensagensErro } from "../../utils/errorUtils";

const AJUDA = {
  titulo: "Cadastro de Máquinas",
  descricao:
    "Cadastre aqui os equipamentos e veículos (tratores, colheitadeiras, implementos etc.) usados na fazenda. Esse cadastro é usado depois nas Atividades para registrar qual máquina foi utilizada em cada operação, além de servir de base para o cálculo de custos por hora e por deslocamento.",
  secoes: [
    {
      titulo: "Dados da Máquina",
      campos: [
        {
          nome: "Fazenda",
          obrigatorio: true,
          explicacao: "Fazenda à qual essa máquina pertence.",
        },
        {
          nome: "Tipo de Máquina",
          obrigatorio: true,
          explicacao:
            "Categoria do equipamento (ex.: Trator, Colheitadeira, Pulverizador). Define quais fabricantes aparecem na lista seguinte. Use o + para cadastrar um novo tipo sem sair desta tela.",
        },
        {
          nome: "Fabricante",
          obrigatorio: true,
          explicacao:
            "Marca do equipamento. A lista de opções depende do Tipo de Máquina selecionado antes. Use o + para cadastrar um novo fabricante sem sair desta tela.",
        },
        {
          nome: "Modelo",
          obrigatorio: true,
          explicacao:
            "Modelo específico do equipamento (ex.: 'Série 5000', '8R'), para diferenciar máquinas do mesmo fabricante.",
        },
        {
          nome: "Ano de Fabricação",
          obrigatorio: true,
          explicacao:
            "Ano de fabricação da máquina, em 4 dígitos. Ajuda a identificar o equipamento e planejar manutenções.",
        },
        {
          nome: "Km da Aquisição",
          obrigatorio: true,
          explicacao:
            "Quilometragem registrada no momento da compra, usada como referência inicial de uso do equipamento.",
        },
        {
          nome: "Custo por Hora",
          obrigatorio: true,
          explicacao:
            "Valor em R$ que representa o custo de operação da máquina por hora. É usado para calcular o custo das Atividades em que essa máquina é utilizada.",
        },
        {
          nome: "Velocidade Média",
          obrigatorio: true,
          explicacao:
            "Velocidade média de operação da máquina, em km/h. Ajuda a estimar o tempo necessário para cobrir uma área nas Atividades.",
        },
      ],
    },
  ],
  dicas: [
    "O cadastro de Tipos e Fabricantes é compartilhado entre todas as fazendas do contrato — cadastre uma vez e reuse.",
    "Excluir uma máquina com atividades vinculadas pode não ser permitido — verifique o histórico de uso antes de excluir.",
    "O botão + ao lado de Tipo e Fabricante abre um modal de cadastro rápido: o que você já preencheu no formulário é preservado.",
  ],
};

export default function GestaoMaquinas() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [maquinas, setMaquinas] = useState([]);
  const [maquinasFiltradas, setMaquinasFiltradas] = useState([]);
  const [editando, setEditando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [fazendas, setFazendas] = useState([]);
  const [tiposMaquina, setTiposMaquina] = useState([]);
  const [fabricantes, setFabricantes] = useState([]);

  const [mostrarModalTipo, setMostrarModalTipo] = useState(false);
  const [mostrarModalFabricante, setMostrarModalFabricante] = useState(false);
  const [mostrarModalFazenda, setMostrarModalFazenda] = useState(false);

  const [filtros, setFiltros] = useState({ busca: "" });

  const [formData, setFormData] = useState({
    fazenda: "",
    tipo_maquina: "",
    fabricante: "",
    modelo: "",
    ano_fabricacao: "",
    km_aquisicao: "",
    custo_hora: "",
    velocidade_media: "",
  });

  const tiposAtivosOrdenados = useMemo(
    () => [...tiposMaquina].filter((t) => t.ativo !== false).sort((a, b) => (a.nome || "").localeCompare(b.nome || "", "pt-BR")),
    [tiposMaquina]
  );

  const fabricantesAtivos = useMemo(
    () => fabricantes.filter((f) => f.ativo !== false),
    [fabricantes]
  );

  const fabricantesFiltrados = useMemo(() => {
    if (!formData.tipo_maquina) return [];
    return fabricantesAtivos
      .filter((f) => Array.isArray(f.tipos_maquina) && f.tipos_maquina.includes(Number(formData.tipo_maquina)))
      .sort((a, b) => (a.nome || "").localeCompare(b.nome || "", "pt-BR"));
  }, [fabricantesAtivos, formData.tipo_maquina]);

  const carregarFazendas = async () => {
    try {
      const response = await repositorioFazenda.listar();
      setFazendas(response.data.results || response.data);
    } catch (error) {
      console.error("Erro ao carregar fazendas:", error);
    }
  };

  const carregarTipos = async () => {
    try {
      const response = await repositorioTipoMaquina.listar();
      const dados = response.data.results || response.data;
      setTiposMaquina(dados);
      return dados;
    } catch (error) {
      console.error("Erro ao carregar tipos de máquina:", error);
      return [];
    }
  };

  const carregarFabricantes = async () => {
    try {
      const response = await repositorioFabricanteMaquina.listar();
      const dados = response.data.results || response.data;
      setFabricantes(dados);
      return dados;
    } catch (error) {
      console.error("Erro ao carregar fabricantes:", error);
      return [];
    }
  };

  const carregarMaquinas = async () => {
    try {
      setCarregandoLista(true);
      const response = await repositorioMaquina.listar();
      const dados = response.data.results || response.data;
      setMaquinas(dados);
      setMaquinasFiltradas(dados);
    } catch (error) {
      console.error("Erro ao carregar máquinas:", error);
      alert("Erro ao carregar lista de máquinas");
    } finally {
      setCarregandoLista(false);
    }
  };

  useEffect(() => {
    carregarMaquinas();
    carregarFazendas();
    carregarTipos();
    carregarFabricantes();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "tipo_maquina") {
      setFormData((prev) => ({
        ...prev,
        tipo_maquina: value,
        fabricante: "", // limpa fabricante ao trocar de tipo
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validarFormulario = () => {
    if (!formData.fazenda) { alert("Selecione uma fazenda."); return false; }
    if (!formData.tipo_maquina) { alert("Selecione o tipo de máquina."); return false; }
    if (!formData.fabricante) { alert("Selecione o fabricante."); return false; }
    if (!formData.modelo.trim()) { alert("Informe o modelo da máquina."); return false; }
    if (!formData.ano_fabricacao || formData.ano_fabricacao.toString().length !== 4) { alert("Informe um ano de fabricação válido (4 dígitos)."); return false; }
    if (!formData.km_aquisicao || Number(formData.km_aquisicao) < 0) { alert("Informe a quilometragem de aquisição."); return false; }
    if (!formData.custo_hora || Number(formData.custo_hora) <= 0) { alert("Informe o custo por hora válido."); return false; }
    if (!formData.velocidade_media || Number(formData.velocidade_media) <= 0) { alert("Informe a velocidade média válida."); return false; }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validarFormulario()) return;
    setLoading(true);

    const payload = {
      fazenda: formData.fazenda,
      tipo_maquina: formData.tipo_maquina,
      fabricante: formData.fabricante,
      modelo: formData.modelo,
      ano_fabricacao: formData.ano_fabricacao,
      km_aquisicao: formData.km_aquisicao,
      custo_hora: formData.custo_hora,
      velocidade_media: formData.velocidade_media,
    };

    try {
      if (editando) {
        await repositorioMaquina.atualizar(editandoId, payload);
        alert("Máquina atualizada com sucesso!");
      } else {
        await repositorioMaquina.criar(payload);
        alert("Máquina cadastrada com sucesso!");
      }
      handleCancel();
      carregarMaquinas();
    } catch (error) {
      console.error("Erro:", error);
      const msgs = extrairMensagensErro(error);
      alert(msgs.join("\n"));
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (maquina) => {
    setFormData({
      fazenda: maquina.fazenda?.id || maquina.fazenda || "",
      tipo_maquina: maquina.tipo_maquina || "",
      fabricante: maquina.fabricante || "",
      modelo: maquina.modelo || "",
      ano_fabricacao: maquina.ano_fabricacao || "",
      km_aquisicao: maquina.km_aquisicao || "",
      custo_hora: maquina.custo_hora || "",
      velocidade_media: maquina.velocidade_media || "",
    });
    setEditando(true);
    setEditandoId(maquina.id);
    setMostrarFormulario(true);
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
  };

  const handleNovoMaquina = () => {
    handleCancel();
    setMostrarFormulario(true);
  };

  const handleCancel = () => {
    setFormData({
      fazenda: "",
      tipo_maquina: "",
      fabricante: "",
      modelo: "",
      ano_fabricacao: "",
      km_aquisicao: "",
      custo_hora: "",
      velocidade_media: "",
    });
    setEditando(false);
    setEditandoId(null);
    setMostrarFormulario(false);
  };

  const handleExcluir = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir esta máquina? Esta ação não pode ser desfeita.")) return;
    try {
      await repositorioMaquina.excluir(id);
      alert("Máquina excluída com sucesso!");
      carregarMaquinas();
      if (editandoId === id) handleCancel();
    } catch (error) {
      console.error("Erro ao excluir:", error);
      alert("Erro ao excluir máquina.");
    }
  };

  const aplicarFiltros = () => {
    let filtrados = [...maquinas];
    if (filtros.busca) {
      const buscaLower = filtros.busca.toLowerCase();
      filtrados = filtrados.filter((m) =>
        (m.veiculos || "").toLowerCase().includes(buscaLower) ||
        (m.fabricante_nome_fk || "").toLowerCase().includes(buscaLower) ||
        (m.modelo || "").toLowerCase().includes(buscaLower) ||
        (m.fazenda_nome || "").toLowerCase().includes(buscaLower)
      );
    }
    setMaquinasFiltradas(filtrados);
  };

  const limparFiltros = () => {
    setFiltros({ busca: "" });
    setMaquinasFiltradas(maquinas);
  };

  const formatarCustoHora = (custo) => (!custo && custo !== 0 ? "—" : `R$ ${Number(custo).toFixed(2)}/h`);
  const formatarKm = (km) => (!km && km !== 0 ? "—" : `${Number(km).toLocaleString("pt-BR")} km`);
  const formatarVelocidade = (vel) => (!vel && vel !== 0 ? "—" : `${Number(vel).toFixed(1)} km/h`);

  return (
    <Layout>
      <div className="p-6 font-medium max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
              Gestão de Máquinas <GiFarmTractor size={38} />
            </h2>
            <AjudaTela {...AJUDA} />
          </div>
          {!mostrarFormulario && (
            <button
              onClick={handleNovoMaquina}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center gap-2 shadow-md"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Nova Máquina
            </button>
          )}
        </div>

        {mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">
                {editando ? "Editar Máquina" : "Cadastrar Nova Máquina"}
              </h3>
              <button
                onClick={handleCancel}
                className="text-gray-500 hover:text-gray-700"
                title="Fechar formulário"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                {/* Fazenda */}
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Fazenda *</label>
                  <div className="flex items-center gap-2">
                    <select
                      name="fazenda"
                      value={formData.fazenda}
                      onChange={handleChange}
                      className="w-64 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      required
                    >
                      <option value="">Selecione uma fazenda</option>
                      {fazendas.map((f) => (
                        <option key={f.id} value={f.id}>{f.nome}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setMostrarModalFazenda(true)}
                      title="Cadastrar nova Fazenda"
                      className="p-2 hover:bg-gray-200 rounded"
                    >
                      <LuCopyPlus size={26} className="text-green-600 hover:text-green-800" />
                    </button>
                  </div>
                </div>

                {/* Tipo de Máquina + Fabricante */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Tipo de Máquina *</label>
                    <div className="flex items-center gap-2">
                      <select
                        name="tipo_maquina"
                        value={formData.tipo_maquina}
                        onChange={handleChange}
                        className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        required
                      >
                        <option value="">Selecione o tipo de máquina</option>
                        {tiposAtivosOrdenados.map((t) => (
                          <option key={t.id} value={t.id}>{t.nome}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setMostrarModalTipo(true)}
                        title="Cadastrar novo Tipo de Máquina"
                        className="p-2 hover:bg-gray-200 rounded"
                      >
                        <LuCopyPlus size={26} className="text-green-600 hover:text-green-800" />
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Fabricante *</label>
                    <div className="flex items-center gap-2">
                      <select
                        name="fabricante"
                        value={formData.fabricante}
                        onChange={handleChange}
                        disabled={!formData.tipo_maquina}
                        className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300 bg-white"
                        required
                      >
                        {!formData.tipo_maquina ? (
                          <option value="">Selecione o tipo de máquina primeiro</option>
                        ) : fabricantesFiltrados.length > 0 ? (
                          <>
                            <option value="">Selecione o fabricante</option>
                            {fabricantesFiltrados.map((f) => (
                              <option key={f.id} value={f.id}>{f.nome}</option>
                            ))}
                          </>
                        ) : (
                          <option value="">Nenhum fabricante encontrado para este tipo</option>
                        )}
                      </select>
                      <button
                        type="button"
                        onClick={() => setMostrarModalFabricante(true)}
                        disabled={!formData.tipo_maquina}
                        title={formData.tipo_maquina ? "Cadastrar novo Fabricante" : "Selecione um tipo de máquina primeiro"}
                        className={`p-2 rounded ${formData.tipo_maquina ? "hover:bg-gray-200" : "opacity-40 cursor-not-allowed"}`}
                      >
                        <LuCopyPlus size={26} className="text-green-600 hover:text-green-800" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Modelo + Ano */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Modelo *</label>
                    <input
                      type="text"
                      name="modelo"
                      value={formData.modelo}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      placeholder="Ex: Série 5000, 8R, etc."
                      required
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Ano de Fabricação *</label>
                    <input
                      type="number"
                      name="ano_fabricacao"
                      value={formData.ano_fabricacao}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded w-32 focus:ring-2 focus:ring-green-300"
                      placeholder="AAAA"
                      min="1900"
                      max={new Date().getFullYear()}
                      required
                    />
                  </div>
                </div>

                {/* Km, Custo e Velocidade */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Km da Aquisição *</label>
                    <div className="flex items-center border border-green-500 rounded">
                      <input
                        type="number"
                        name="km_aquisicao"
                        value={formData.km_aquisicao}
                        onChange={handleChange}
                        className="w-full p-2 rounded-l focus:ring-2 focus:ring-green-300"
                        required step="0.01" min="0" placeholder="0"
                      />
                      <span className="px-3 text-gray-700 bg-gray-100 rounded-r">km</span>
                    </div>
                  </div>
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Custo por Hora *</label>
                    <div className="flex items-center border border-green-500 rounded">
                      <input
                        type="number"
                        name="custo_hora"
                        value={formData.custo_hora}
                        onChange={handleChange}
                        className="w-full p-2 rounded-l focus:ring-2 focus:ring-green-300"
                        required step="0.01" min="0" placeholder="0,00"
                      />
                      <span className="px-3 text-gray-700 bg-gray-100 rounded-r">R$/h</span>
                    </div>
                  </div>
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Velocidade Média *</label>
                    <div className="flex items-center border border-green-500 rounded">
                      <input
                        type="number"
                        name="velocidade_media"
                        value={formData.velocidade_media}
                        onChange={handleChange}
                        className="w-full p-2 rounded-l focus:ring-2 focus:ring-green-300"
                        required step="0.01" min="0" placeholder="0,00"
                      />
                      <span className="px-3 text-gray-700 bg-gray-100 rounded-r">km/h</span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors disabled:bg-blue-300"
                  >
                    {loading ? "Salvando..." : (editando ? "Atualizar" : "Cadastrar")}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        {!mostrarFormulario && (
          <>
            <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filtros de Busca
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Buscar por Máquina, Fabricante, Modelo ou Fazenda</label>
                  <input
                    type="text"
                    placeholder="Digite o tipo, fabricante, modelo ou fazenda..."
                    value={filtros.busca}
                    onChange={(e) => setFiltros({ ...filtros, busca: e.target.value })}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-4">
                <button onClick={aplicarFiltros} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors">Aplicar Filtros</button>
                <button onClick={limparFiltros} className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors">Limpar Filtros</button>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <p className="text-gray-600">{maquinasFiltradas.length} máquina(s) encontrada(s)</p>
              </div>
              {carregandoLista ? (
                <div className="p-8 text-center text-gray-500">Carregando...</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Código</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Tipo</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Fabricante</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Modelo</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Ano</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Km Aquisição</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Custo/h</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Vel. Média</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Fazenda</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {maquinasFiltradas.map((m) => (
                        <tr key={m.id} className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
                          <td className="p-3 text-sm font-mono">{m.id}</td>
                          <td className="p-3 text-sm font-medium">{m.veiculos || m.tipo_maquina_nome || "—"}</td>
                          <td className="p-3 text-sm">{m.fabricante_legado || m.fabricante_nome_fk || "—"}</td>
                          <td className="p-3 text-sm">{m.modelo}</td>
                          <td className="p-3 text-sm">{m.ano_fabricacao}</td>
                          <td className="p-3 text-sm">{formatarKm(m.km_aquisicao)}</td>
                          <td className="p-3 text-sm">{formatarCustoHora(m.custo_hora)}</td>
                          <td className="p-3 text-sm">{formatarVelocidade(m.velocidade_media)}</td>
                          <td className="p-3 text-sm">{m.fazenda_nome || "-"}</td>
                          <td className="p-3">
                            <div className="flex gap-2">
                              <button onClick={() => handleEditar(m)} className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm">Editar</button>
                              <button onClick={() => handleExcluir(m.id)} className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm">Excluir</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {maquinasFiltradas.length === 0 && (
                        <tr>
                          <td colSpan="10" className="p-8 text-center text-gray-500">
                            {filtros.busca ? "Nenhuma máquina encontrada com os filtros selecionados." : "Nenhuma máquina cadastrada."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <ModalCadastroTipoMaquina
        isOpen={mostrarModalTipo}
        onClose={() => setMostrarModalTipo(false)}
        onSuccess={async (novo) => {
          await carregarTipos();
          setFormData((prev) => ({ ...prev, tipo_maquina: novo.id, fabricante: "" }));
        }}
      />
      <ModalCadastroFabricanteMaquina
        isOpen={mostrarModalFabricante}
        onClose={() => setMostrarModalFabricante(false)}
        tiposMaquina={tiposMaquina}
        tipoSelecionadoId={formData.tipo_maquina}
        onSuccess={async (novo) => {
          await carregarFabricantes();
          setFormData((prev) => ({ ...prev, fabricante: novo.id }));
        }}
      />
      <ModalCadastroFazenda
        isOpen={mostrarModalFazenda}
        onClose={() => setMostrarModalFazenda(false)}
        onSuccess={async (nova) => {
          await carregarFazendas();
          setFormData((prev) => ({ ...prev, fazenda: nova.id }));
        }}
      />
    </Layout>
  );
}
